import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Loader2 } from "lucide-react";
import { ALL_PERMISSIONS } from "@/lib/permissions";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, roles } = useAuth();
  const location = useLocation();
  const subscription = useSubscription();

  if (loading || subscription.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  // Skip feature gating for non-dashboard routes (admin, support-panel, accountant)
  const isDashboardRoute = location.pathname.startsWith("/dashboard");
  if (!isDashboardRoute) return <>{children}</>;

  // Skip for parents, super_admins, and system roles
  const isBypassed = roles.some(r =>
    ["super_admin", "parent", "support", "accountant", "call_center"].includes(r)
  );
  if (isBypassed) return <>{children}</>;

  // Find the feature key for the current path
  const currentPath = location.pathname;
  const matchedPermission = ALL_PERMISSIONS.find(p => {
    if (p.url === "/dashboard") return currentPath === "/dashboard";
    return currentPath.startsWith(p.url);
  });

  // If we found a permission key and it's not accessible, redirect to subscription
  if (matchedPermission && !subscription.canAccess(matchedPermission.key)) {
    return <Navigate to="/dashboard/subscription" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
